/**
 * Renovacao do access token (JWT curto) pelo refresh token (cookie httpOnly de 30 dias).
 *
 * Fluxo de toda chamada autenticada (`fetchWithAuth`):
 *   1. manda com o access token atual;
 *   2. 401 -> `POST /auth/refresh` (o navegador leva o cookie) -> refaz a MESMA chamada
 *      com o token novo;
 *   3. so encerra a sessao (volta ao login) se o refresh for recusado ou se a segunda
 *      tentativa tambem der 401.
 *
 * Varias chamadas recebendo 401 juntas (o Dashboard dispara ~6) compartilham UMA
 * renovacao — sem isso cada uma trocaria o refresh e o servidor leria as seguintes
 * como reuso.
 *
 * Modulo sem dependencia de store: `authStore` registra os handlers (evita o ciclo
 * api.ts <-> authStore.ts, o mesmo motivo de `tokenHolder`).
 */
import { setToken } from '@/lib/tokenHolder'

const API_BASE = `${import.meta.env.VITE_API_URL || ''}/api/v1`
const REFRESH_PATH = '/auth/refresh'

/** Rotas de sessao: 401 nelas e resposta final, nunca motivo para renovar. */
const SESSION_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/verify_otp', REFRESH_PATH, '/auth/logout']

export type RefreshOutcome =
  | { status: 'ok'; token: string }
  | { status: 'rejected' } // refresh vencido, revogado ou reusado: sessao acabou
  | { status: 'error' } // rede/servidor: nao da para concluir nada

interface SessionHandlers {
  onTokenRefreshed: (token: string) => void
  onSessionExpired: () => void
}

let handlers: SessionHandlers | null = null
let inflight: Promise<RefreshOutcome> | null = null

export function registerSessionHandlers(next: SessionHandlers): void {
  handlers = next
}

export function isSessionEndpoint(endpoint: string): boolean {
  const path = endpoint.split('?')[0]
  return SESSION_ENDPOINTS.includes(path)
}

export function refreshAccessToken(): Promise<RefreshOutcome> {
  if (!inflight) {
    inflight = requestRefresh().finally(() => {
      inflight = null
    })
  }
  return inflight
}

export function expireSession(): void {
  setToken(null)
  handlers?.onSessionExpired()
}

/**
 * `fetch` autenticado com renovacao e UMA nova tentativa. Devolve a resposta final
 * para o chamador tratar como sempre tratou (inclusive o 401, que segue virando
 * `ApiError`).
 */
export async function fetchWithAuth(
  endpoint: string,
  url: string,
  init: RequestInit,
  currentToken: () => string | null,
): Promise<Response> {
  const sentToken = currentToken()
  const response = await fetch(url, withBearer(init, sentToken))

  // Sem token nao havia sessao a renovar (tela publica); rota de sessao responde por si.
  if (response.status !== 401 || !sentToken || isSessionEndpoint(endpoint)) {
    return response
  }

  const outcome = await refreshAccessToken()
  if (outcome.status === 'rejected') {
    expireSession()
    return response
  }
  if (outcome.status === 'error') return response

  const retry = await fetch(url, withBearer(init, outcome.token))
  if (retry.status === 401) expireSession()
  return retry
}

function withBearer(init: RequestInit, token: string | null): RequestInit {
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  else headers.delete('Authorization')
  return { ...init, credentials: 'include', headers }
}

async function requestRefresh(): Promise<RefreshOutcome> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}${REFRESH_PATH}`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    })
  } catch {
    return { status: 'error' }
  }

  if (response.status === 401) return { status: 'rejected' }
  if (!response.ok) return { status: 'error' }

  try {
    const data = (await response.json()) as { token?: unknown }
    if (typeof data.token !== 'string' || data.token === '') return { status: 'error' }
    setToken(data.token)
    handlers?.onTokenRefreshed(data.token)
    return { status: 'ok', token: data.token }
  } catch {
    return { status: 'error' }
  }
}

/** So para testes: zera o estado do modulo. */
export function resetAuthSessionForTests(): void {
  handlers = null
  inflight = null
  setToken(null)
}
